import { OPERATIONS } from './operations';

// ---------------------------------------------------------------------------
// THE MODEL SEAM.
//
// This is where the call to Claude or Gemini goes. It is a rule-based stand-in
// so the whole flow — prompt, interpretation, scope check, apply, refuse — can
// be walked and argued about before an API key exists.
//
// WHAT THE REAL IMPLEMENTATION REPLACES: the body of interpret(). Nothing else.
// The contract either side of it is already the real one:
//
//     in   { prompt, context }        a sentence and which page asked
//     out  { operation } | { refusal }
//
// WHAT THE REAL IMPLEMENTATION MUST KEEP:
//
//   1. NO CUSTOMER DATA IN THE PROMPT. Send the sentence, the operation
//      vocabulary and the page context — module, field labels, version label.
//      Never rows, never amounts. "Increase OPEX by 5%" does not require the
//      model to know what OPEX currently is, and keeping it that way is what
//      makes the data-residency answer "no, only the instruction leaves" and
//      what leaves prompt injection with nothing to carry.
//
//   2. STRUCTURED OUTPUT, NOT PROSE. Tool use / JSON schema, constrained to the
//      operations below. A free-text response that is then parsed is how SQL
//      gets back in through the side door.
//
//   3. REFUSE RATHER THAN IMPROVISE. If the sentence does not map to a known
//      operation, say so and list what is possible. An assistant that guesses at
//      a budget is worse than one that admits a gap.
//
//   4. NEVER AUTO-APPLY. The caller decides; this function only proposes.
// ---------------------------------------------------------------------------

const WORD_NUMBERS = {
  five: 5, ten: 10, fifteen: 15, twenty: 20, twentyfive: 25, thirty: 30,
  forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100,
  half: 0.5, one: 1, two: 2, three: 3, four: 4, six: 6, seven: 7, eight: 8, nine: 9,
};

const num = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/[, ]/g, '');
  if (WORD_NUMBERS[s] != null) return WORD_NUMBERS[s];
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// Simulated latency, so the interpreting state is a real state that has been
// looked at rather than a flash nobody ever sees.
const think = (ms = 650) => new Promise((r) => setTimeout(r, ms));

export async function interpret(prompt, context = {}) {
  await think();

  const p = String(prompt || '').toLowerCase().trim();
  if (!p) return { refusal: 'Nothing was said.', suggestions: SUGGESTIONS };

  const fields = context.fields || [];
  const fieldFor = (txt) =>
    fields.find((f) => txt.includes(f.label.toLowerCase())) || fields[0];

  // ---- Requests that are deliberately out of scope for v1 ----------------
  // Reading is a different feature with a different risk profile: it genuinely
  // needs data in the prompt. Refused explicitly rather than half-answered.
  if (/\b(what|how much|show me|tell me|which|why|report on|compare)\b/.test(p) && !/\b(increase|decrease|set|copy|spread|apply)\b/.test(p)) {
    return {
      refusal: 'I can change figures on this page, but I cannot answer questions about them yet.',
      detail: 'Asking about your data means sending it to the model. That is a separate feature with its own approval, and it is not switched on.',
      suggestions: SUGGESTIONS,
    };
  }

  if (/\b(delete|remove|drop|clear all|wipe|truncate)\b/.test(p)) {
    return {
      refusal: 'I will not delete anything.',
      detail: 'Removing lines is not one of the operations I can perform. Delete them yourself if that is really what you want.',
      suggestions: SUGGESTIONS,
    };
  }

  // ---- copy from another version ----------------------------------------
  if (/\b(copy|take|start|base|pull)\b/.test(p) && /\b(last year|previous|version|budget|draft|forecast|fy\d+)\b/.test(p)) {
    const basisLabel = /last year|previous|fy2?5|fy25/.test(p) ? 'last year’s budget' : 'another version';
    // "copy last year and add 5%" is one intent, not two — the escalation rides
    // on the copy so the user confirms once.
    const pct = matchPct(p);
    return {
      operation: {
        op: 'copy_from_version',
        field: fieldFor(p)?.key,
        field_label: fieldFor(p)?.label,
        basis: 'version:previous',
        basis_label: basisLabel,
        then_escalate: pct == null ? null : { method: 'pct', value: pct },
        filter: matchFilter(p),
      },
      confidence: pct == null ? 'high' : 'medium',
      note: pct == null ? null
        : `Read as: copy ${basisLabel}, then increase it by ${pct}%. If you meant to escalate what is already here, say "increase by ${pct}%" instead.`,
    };
  }

  // ---- price index --------------------------------------------------------
  if (/\b(index|indexed|cpi|ppi|diesel|fuel|inflation)\b/.test(p)) {
    const idx = /diesel|fuel/.test(p) ? 'DIESEL' : /ppi/.test(p) ? 'PPI' : 'CPI';
    return {
      operation: {
        op: 'apply_index',
        field: fieldFor(p)?.key,
        field_label: fieldFor(p)?.label,
        index_code: idx,
        index_label: idx === 'DIESEL' ? 'diesel' : idx,
        // No filter derived here. "Escalate against the diesel index" names the
        // INDEX, not a set of rows — reading it as "lines matching diesel"
        // silently narrows the change to nothing and reports success.
        filter: null,
      },
      confidence: 'high',
    };
  }

  // ---- spread -------------------------------------------------------------
  if (/\b(spread|split|distribute|phase)\b/.test(p)) {
    const v = matchAmount(p);
    if (v == null) {
      return { refusal: 'I could not tell what total to spread.', detail: 'Try "spread 1 200 000 across the year".', suggestions: SUGGESTIONS };
    }
    const f = fieldFor(p);
    return { operation: { op: 'spread', field: f?.key, field_label: f?.label, value: v, filter: matchFilter(p) }, confidence: 'high' };
  }

  // ---- set ----------------------------------------------------------------
  // "make it" only counts as a set when a number follows. Without that guard
  // "make it better" was read as a set with a missing value and refused for the
  // wrong reason — a confusing refusal is nearly as bad as a wrong action,
  // because the user changes the wrong half of the sentence and tries again.
  const setVerb = /\b(set|change to|fix at)\b/.test(p) || /\bmake it\b[^.]*\d/.test(p);
  if (setVerb) {
    const v = matchAmount(p);
    if (v == null) {
      return { refusal: 'I could not tell what to set the value to.', detail: 'Try "set the rate to 42.50".', suggestions: SUGGESTIONS };
    }
    const f = fieldFor(p);
    return { operation: { op: 'set', field: f?.key, field_label: f?.label, value: v, filter: matchFilter(p) }, confidence: 'high' };
  }

  // ---- escalate -----------------------------------------------------------
  const pct = matchPct(p);
  if (pct != null) {
    const down = /\b(decrease|reduce|cut|lower|less|down|drop)\b/.test(p);
    const f = fieldFor(p);
    return {
      operation: {
        op: 'escalate', method: 'pct', value: down ? -Math.abs(pct) : Math.abs(pct),
        field: f?.key, field_label: f?.label, filter: matchFilter(p),
      },
      confidence: 'high',
    };
  }

  const abs = /\b(by|add|plus|minus)\b/.test(p) ? matchAmount(p) : null;
  if (abs != null) {
    const down = /\b(decrease|reduce|cut|lower|minus|less)\b/.test(p);
    const f = fieldFor(p);
    return {
      operation: {
        op: 'escalate', method: 'absolute', value: down ? -Math.abs(abs) : Math.abs(abs),
        field: f?.key, field_label: f?.label, filter: matchFilter(p),
      },
      // Deliberately lower: "add 5" is far more often a mistyped percentage than
      // a five-rand escalation, and the confirmation panel should say so.
      confidence: 'medium',
      note: 'Read as a fixed amount per line, not a percentage. Say "5%" if you meant a percentage.',
    };
  }

  return {
    refusal: 'I could not turn that into a change I know how to make.',
    detail: `I can ${Object.values(OPERATIONS).map((o) => o.summary.toLowerCase()).join('; ')}.`,
    suggestions: SUGGESTIONS,
  };
}

function matchPct(p) {
  const m = p.match(/(-?[\d.,]+)\s*(?:%|percent|per cent)/)
    || p.match(/\b(?:by|of)\s+([a-z]+)\s*(?:%|percent|per cent)/);
  return m ? num(m[1]) : null;
}

function matchAmount(p) {
  const m = p.match(/\b(?:to|by|add|plus|minus|spread|of)\s+(?:r|zar)?\s*([\d][\d.,\s]*)/);
  if (m) return num(m[1]);
  const bare = p.match(/\b([\d][\d.,]{2,})\b/);
  return bare ? num(bare[1]) : null;
}

// A scope inside the page — "the electricity lines", "just Cape Town". Kept as
// TEXT rather than a resolved id: the model does not know the tenant's codes and
// should not be invited to guess at them.
function matchFilter(p) {
  const m = p.match(/\b(?:for|on|just|only|the)\s+([a-z][a-z\s]{2,24}?)\s+(?:lines?|rows?|items?|costs?)\b/);
  if (m) return { text: m[1].trim() };
  const named = p.match(/\b(electricity|cleaning|security|water|fuel|diesel|packaging|labour|maintenance|freight|rent)\b/);
  return named ? { text: named[1] } : null;
}

export const SUGGESTIONS = [
  'Increase everything by 5%',
  'Take last year’s budget and add 6%',
  'Reduce the cleaning lines by 10%',
  'Set the rate to 42.50',
  'Escalate against the diesel index',
];
